<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { AlternativeAttributeValueChangeReadModel, AttributeReadModel } from "@Evaluation/ValueAnalysis/Domain/readmodels";
    import { SaveToAttributeDialogViewModel } from "@Evaluation/ValueAnalysis/Components/SaveToAttributeDialog";

    @Component({
        props: {
            viewModel: { type: Object, required: true }
        }
    })
    export default class SaveToAttributeDialog extends Vue
    {
        get vm(): SaveToAttributeDialogViewModel { return this.$props.viewModel as SaveToAttributeDialogViewModel; }
        get opened(): boolean { return this.vm?.opened ?? false; }

        get saving(): boolean { return this.vm.saving; }
        get allAttributes(): AttributeReadModel[] { return this.vm.attributes; }
        get attributes(): AttributeReadModel[] { return this.allAttributes.filter(a => a.IsNumeric === true && a.IsManual === true && a.IsMultivalue === false); }
        get duplicatedName(): boolean { return this.allAttributes.some(a => a.Name.toLowerCase() === this.newAttributeName?.toLowerCase()); } 
        get attributeName(): string { return this.useExistingAttribute === true ? this.existingAttribute.Name : this.newAttributeName; }
        get changes(): AlternativeAttributeValueChangeReadModel[] { return this.vm.changes; }

        get step(): string { return this.vm.step; }
        set step(val: string) { this.vm.step = val; }

        get newAttributeName(): string { return this.vm.newAttributeName; }
        set newAttributeName(val: string) { this.vm.newAttributeName = val; }

        get existingAttribute(): AttributeReadModel | null { return this.vm.existingAttribute; }
        set existingAttribute(val: AttributeReadModel | null) { this.vm.existingAttribute = val; }
                
        get useExistingAttribute(): boolean { return this.vm.useExistingAttribute; }
        set useExistingAttribute(val: boolean) { this.vm.useExistingAttribute = val; }

        saveChanges()
        {
            if (this.useExistingAttribute === true)
            {
                this.$emit('save-existing-attribute', this.existingAttribute.Id, this.changes.filter(a => a.NewValue != null).map(a => `${a.Id}:${a.NewValue}`));
            }
            else
            {                
                this.$emit('save-new-attribute', this.newAttributeName, this.changes.filter(a => a.NewValue != null).map(a => `${a.Id}:${a.NewValue}`));
            }
        }

        showStart()
        {
            this.step = "start";
            this.vm.changes = null;
        }

        showPreview()
        {
            if (this.useExistingAttribute === true && this.existingAttribute == null)
            {
                return;
            }

            const attrId = this.useExistingAttribute === true ? this.existingAttribute.Id : null;

            this.step = "preview";
            this.vm.showChanges(attrId);
        }

        close()
        {
            this.vm.close();
        }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div v-if="step === 'start'" class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Save to attribute</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="card-deck">
                            <div class="card" :class="{'border-primary' : useExistingAttribute === false }">
                                <div class="card-header pointer" @click="useExistingAttribute = false" :class="{'text-white bg-primary' : useExistingAttribute === false }">
                                    <i class="fa-regular" aria-hidden="true" :class="{'fa-square': useExistingAttribute === true, 'fa-square-check': useExistingAttribute === false}"></i>
                                    Create new attribute 
                                </div>
                                <div class="card-body">
                                    <div class="form-group">
                                        <input type="text" class="form-control" v-model.trim="newAttributeName">
                                        <small v-if="duplicatedName === true" class="text-danger">Sorry, this name is already being used. Please change the name to a unique name.</small>
                                        <small class="form-text text-muted">Values will be saved as a new attribute in "Value scores" section.</small>
                                    </div>
                                </div>
                            </div>
                            <div class="card" :class="{'border-primary' : useExistingAttribute === true}">
                                <div class="card-header pointer" @click="useExistingAttribute = true" :class="{'text-white bg-primary' : useExistingAttribute === true }">
                                    <i class="fa-regular" aria-hidden="true" :class="{'fa-square': useExistingAttribute === false, 'fa-square-check': useExistingAttribute === true}"></i>
                                    Use existing attribute
                                </div>
                                <div class="card-body">
                                    <div class="form-group">
                                        <select v-model="existingAttribute" class="form-control">
                                            <option :value="null">Choose...</option>
                                            <option v-for="attr in attributes" :value="attr">{{attr.Name}}</option>

                                        </select>
                                        <small class="form-text text-muted">
                                            Values will be saved to the existing attribute.
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="showPreview" type="button" class="btn btn-primary">Preview</button>
                        <button type="button" class="btn btn-outline-secondary" @click="close">Cancel</button>
                    </div>
                </div>
                <div v-if="step === 'preview'" class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Preview</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <table class="table table-bordered table-hover">
                            <thead>
                                <tr>
                                    <th>{{attributeName}}</th>
                                    <th>Value</th>
                                </tr>
                            </thead>
                            <tbody v-if="changes">
                                <tr v-for="a in changes" :key="a.Id">
                                    <td>{{a.Name}}</td>
                                    <td class="text-center">
                                        <span>{{a.NewValue}}</span>
                                        <small v-if="a.OldValue != null">
                                            <br />
                                            (Current: <span class="text-danger">{{a.OldValue}}</span>)
                                        </small>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                    </div>

                    <div class="modal-footer d-flex justify-content-between">
                        <button type="button" class="btn btn-outline-secondary" @click="showStart">Back</button>
                        <div>
                            <button v-if="saving === true" type="button" class="btn btn-primary" @click="saveChanges">
                                <i class="fa fa-spinner fa-spin"></i> Saving...
                            </button>
                            <button v-if="saving === false" type="button" class="btn btn-primary" @click="saveChanges">Save</button>
                            <button type="button" class="btn btn-outline-secondary" @click="close">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>